import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactElement, ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchSession, logout as logoutRequest } from '../api/endpoints';
import { onUnauthenticated } from '../api/client';
import { isUnauthenticated } from '../api/errors';
import { queryKeys } from '../api/query-keys';
import type { Role, SessionResponse } from '../api/schemas';

/**
 * Session state.
 *
 * The session itself lives in an httpOnly cookie — this module never sees a
 * token and never writes to `localStorage`. All it holds is who the server
 * says we are, so the UI can hide controls a role may not use. Authorisation
 * is still enforced on the server for every command; this is presentation.
 */

export type SessionStatus = 'loading' | 'anonymous' | 'authenticated';

export interface SessionValue {
  readonly status: SessionStatus;
  readonly session: SessionResponse | null;
  readonly role: Role | null;
  /** True after a previously valid session stopped being accepted. */
  readonly expired: boolean;
  readonly canOperate: boolean;
  readonly isAdmin: boolean;
  readonly refresh: () => void;
  readonly signOut: () => Promise<void>;
  /**
   * Opens the re-authentication modal and resolves `true` once the operator
   * has re-authenticated, `false` if they cancelled. Callers use it to retry
   * a command that failed with `REAUTH_REQUIRED` / `TOTP_REQUIRED`.
   */
  readonly promptReauth: (reasonRu?: string) => Promise<boolean>;
  /** Internal wiring for the modal; not used by screens. */
  readonly reauthRequest: ReauthRequest | null;
  readonly resolveReauth: (success: boolean) => void;
}

export interface ReauthRequest {
  readonly reasonRu: string;
}

const DEFAULT_REASON = 'Действие требует подтверждения личности.';

const SessionContext = createContext<SessionValue | null>(null);

async function loadSession(signal: AbortSignal): Promise<SessionResponse | null> {
  try {
    return await fetchSession(signal);
  } catch (error) {
    // Not being logged in is a normal state, not a failure to display.
    if (isUnauthenticated(error)) return null;
    throw error;
  }
}

export function SessionProvider({ children }: { children: ReactNode }): ReactElement {
  const queryClient = useQueryClient();
  const [expired, setExpired] = useState(false);
  const [reauthRequest, setReauthRequest] = useState<ReauthRequest | null>(null);
  const resolverRef = useRef<((success: boolean) => void) | null>(null);
  const wasAuthenticatedRef = useRef(false);

  const query = useQuery({
    queryKey: queryKeys.session,
    queryFn: ({ signal }) => loadSession(signal),
    staleTime: 30_000,
    retry: false,
  });

  const session = query.data ?? null;

  useEffect(() => {
    if (session !== null) {
      wasAuthenticatedRef.current = true;
      setExpired(false);
    }
  }, [session]);

  // Any request coming back UNAUTHENTICATED means the cookie is gone or the
  // session was revoked. Drop to the login screen immediately instead of
  // leaving stale prices and a stale position on screen.
  //
  // The session cache is *written*, not invalidated: invalidating would
  // refetch, and if that refetch succeeded while another endpoint kept
  // answering 401 the app would flip between the panel and the login screen
  // in a loop. The operator logs in again, and `refresh()` re-reads it then.
  useEffect(
    () =>
      onUnauthenticated(() => {
        if (wasAuthenticatedRef.current) setExpired(true);
        queryClient.setQueryData(queryKeys.session, null);
      }),
    [queryClient],
  );

  const refresh = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.session });
  }, [queryClient]);

  const signOut = useCallback(async () => {
    try {
      await logoutRequest();
    } finally {
      wasAuthenticatedRef.current = false;
      setExpired(false);
      queryClient.setQueryData(queryKeys.session, null);
      queryClient.clear();
    }
  }, [queryClient]);

  const promptReauth = useCallback(
    (reasonRu?: string) =>
      new Promise<boolean>((resolve) => {
        resolverRef.current = resolve;
        setReauthRequest({ reasonRu: reasonRu ?? DEFAULT_REASON });
      }),
    [],
  );

  const resolveReauth = useCallback(
    (success: boolean) => {
      setReauthRequest(null);
      const resolver = resolverRef.current;
      resolverRef.current = null;
      if (success) refresh();
      if (resolver !== null) resolver(success);
    },
    [refresh],
  );

  const status: SessionStatus = query.isPending
    ? 'loading'
    : session === null
      ? 'anonymous'
      : 'authenticated';

  const role = session?.actor.role ?? null;

  const value = useMemo<SessionValue>(
    () => ({
      status,
      session,
      role,
      expired,
      canOperate: role === 'OPERATOR' || role === 'ADMIN',
      isAdmin: role === 'ADMIN',
      refresh,
      signOut,
      promptReauth,
      reauthRequest,
      resolveReauth,
    }),
    [status, session, role, expired, refresh, signOut, promptReauth, reauthRequest, resolveReauth],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (value === null) {
    throw new Error('useSession использован вне SessionProvider.');
  }
  return value;
}
